/**
 * Select Component
 *
 * A form select component with label and error states.
 */

import { forwardRef, type SelectHTMLAttributes, type ReactNode, useId } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { AlertCircle, ChevronDown } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  helperText?: string; // Alias for hint
  options: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const baseStyles = `
  w-full rounded-lg border bg-white
  appearance-none cursor-pointer
  transition-all duration-200
  focus:outline-none focus:ring-2 focus:ring-offset-0
  disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
`;

const sizeStyles = {
  sm: 'h-8 px-3 pr-8 text-sm',
  md: 'h-10 px-4 pr-10 text-sm',
  lg: 'h-12 px-4 pr-10 text-base',
};

const stateStyles = {
  default: `
    border-slate-300
    hover:border-slate-400
    focus:border-primary-500 focus:ring-primary-500/20
  `,
  error: `
    border-danger-500
    focus:border-danger-500 focus:ring-danger-500/20
  `,
};

// ============================================================================
// Component
// ============================================================================

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      error,
      hint,
      helperText,
      options,
      placeholder,
      size = 'md',
      fullWidth = true,
      id,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const selectId = id || generatedId;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      Logger.debug(LogCategory.UI, 'Select changed', 'Select', {
        selectId,
        name: props.name,
        value: e.target.value,
      });
      onChange?.(e);
    };

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={selectId}
            className="text-sm font-medium text-slate-700"
          >
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              baseStyles,
              sizeStyles[size],
              error ? stateStyles.error : stateStyles.default,
              className
            )}
            onChange={handleChange}
            aria-invalid={!!error}
            aria-describedby={error ? `${selectId}-error` : (hint || helperText) ? `${selectId}-hint` : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
          />
        </div>

        {error && (
          <div
            id={`${selectId}-error`}
            className="flex items-center gap-1.5 text-sm text-danger-600"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {(hint || helperText) && !error && (
          <p
            id={`${selectId}-hint`}
            className="text-sm text-slate-500"
          >
            {hint || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
